import { css } from "@emotion/react";
import { vars } from "@imwebme/clay-token";
import type { ElementType } from "react";
import { match } from "ts-pattern";
import type { IconButtonProps } from "./IconButton.types";

const {
  semantic: { color, typography },
  spacing,
  rounded,
} = vars;

const medium = css`
  padding: ${spacing[3]};
  border-radius: ${rounded.medium};
  ${typography["label-medium"]}
`;

const large = css`
  padding: ${vars.spacing[4]};
  border-radius: ${vars.rounded.medium};

  ${typography["label-large"]}
`;

const small = css`
  padding: ${vars.spacing[2]};
  border-radius: ${vars.rounded.small};

  ${typography["label-medium"]}
`;

const tiny = css`
  padding: ${vars.spacing[15]};
  border-radius: ${vars.rounded.small};

  ${typography["label-small"]}
`;

export const sizeStyles = {
  medium,
  large,
  small,
  tiny,
};

const outlined = () =>
  css({
    "--clay-icon-button-background-color": color.actionSecondary,

    backgroundColor: "var(--clay-icon-button-background-color)",
    "--clay-dot-bg": "var(--clay-icon-button-background-color)",

    color: color.textSecondary,
    svg: {
      color: color.iconSecondary,
    },

    outline: `1px solid ${color.border}`,
    outlineOffset: "-1px",

    "&:hover:not(:disabled)": {
      outlineColor: color.borderHover,
    },

    "&:active:not(:disabled)": {
      outlineColor: color.borderSub,
    },

    "&:disabled": {
      "--clay-icon-button-background-color": color.actionSecondaryDisabled,

      color: color.textDisabled,
      outlineColor: "transparent",
      svg: {
        color: color.textDisabled,
      },

      '&[data-imweb-loading="true"]': {
        "--clay-icon-button-background-color": color.actionSecondary,

        color: color.textSecondary,
        outlineColor: color.borderSub,
        svg: {
          color: color.icon,
        },
      },
    },
  });

const primary = <E extends ElementType>(
  args?: Extract<IconButtonProps<E>, { variant: "primary" }>,
) =>
  css({
    "--clay-icon-button-background-color": args?.tonal
      ? color.actionPrimaryTonal
      : color.actionPrimary,

    backgroundColor: "var(--clay-icon-button-background-color)",
    "--clay-dot-bg": "var(--clay-icon-button-background-color)",

    color: args?.tonal ? color.textSecondary : color.textInverse,
    svg: {
      color: args?.tonal ? color.iconSecondary : color.iconInverse,
    },

    "&:hover:not(:disabled)": {
      "--clay-icon-button-background-color": args?.tonal
        ? color.actionPrimaryTonalHover
        : color.actionPrimaryHover,
    },

    "&:active:not(:disabled)": {
      "--clay-icon-button-background-color": args?.tonal
        ? color.actionPrimaryTonalPressed
        : color.actionPrimaryPressed,
    },

    "&:disabled": {
      color: color.textDisabled,

      '&:not([data-imweb-loading="true"])': {
        "--clay-icon-button-background-color": args?.tonal
          ? color.actionPrimaryTonalDisabled
          : color.actionPrimaryDisabled,

        svg: {
          color: color.textDisabled,
        },
      },

      '&[data-imweb-loading="true"]': {
        "--clay-icon-button-background-color": args?.tonal
          ? color.actionPrimaryTonalPressed
          : color.actionPrimaryPressed,

        color: args?.tonal ? color.textSecondary : color.textInverse,
        svg: {
          color: args?.tonal ? color.icon : color.iconOn,
        },
      },
    },
  });

const secondary = () =>
  css({
    "--clay-icon-button-background-color": "transparent",

    backgroundColor: "var(--clay-icon-button-background-color)",
    "--clay-dot-bg": "var(--clay-icon-button-background-color)",

    color: color.textSecondary,
    svg: {
      color: color.iconSecondary,
    },

    "&:hover:not(:disabled)": {
      "--clay-icon-button-background-color": color.actionSecondaryHover,
    },

    "&:active:not(:disabled)": {
      "--clay-icon-button-background-color": color.actionSecondaryPressed,
    },

    "&:disabled": {
      "--clay-icon-button-background-color": color.actionSecondaryDisabled,

      color: color.textDisabled,
      '&[data-imweb-loading="true"]': {
        "--clay-icon-button-background-color": "transparent",

        color: color.textSecondary,
        svg: {
          color: color.icon,
        },
      },
    },
  });

const critical = <E extends ElementType>(
  args?: Extract<IconButtonProps<E>, { variant: "critical" }>,
) =>
  css({
    "--clay-icon-button-background-color": args?.tonal
      ? color.actionCriticalTonal
      : color.actionCritical,

    backgroundColor: "var(--clay-icon-button-background-color)",
    "--clay-dot-bg": "var(--clay-icon-button-background-color)",

    color: args?.tonal ? color.textCritical : color.textOn,
    svg: {
      color: args?.tonal ? color.iconCritical : color.iconOn,
    },

    "&:hover:not(:disabled)": {
      "--clay-icon-button-background-color": args?.tonal
        ? color.actionCriticalTonalHover
        : color.actionCriticalHover,
    },

    "&:active:not(:disabled)": {
      "--clay-icon-button-background-color": args?.tonal
        ? color.actionCriticalTonalPressed
        : color.actionCriticalPressed,
    },

    "&:disabled": {
      color: color.textDisabled,

      '&:not([data-imweb-loading="true"])': {
        "--clay-icon-button-background-color": args?.tonal
          ? color.actionCriticalTonalDisabled
          : color.actionCriticalDisabled,

        stroke: color.textDisabled,
      },

      '&[data-imweb-loading="true"]': {
        "--clay-icon-button-background-color": args?.tonal
          ? color.actionCriticalTonalPressed
          : color.actionCriticalPressed,

        color: args?.tonal ? color.textCritical : color.textOn,
        svg: {
          color: args?.tonal ? color.iconCritical : color.iconOn,
        },
      },
    },
  });

const accent = <E extends ElementType>(args?: Extract<IconButtonProps<E>, { variant: "accent" }>) =>
  css({
    "--clay-icon-button-background-color": args?.tonal
      ? color.actionAccentTonal
      : color.actionAccent,

    backgroundColor: "var(--clay-icon-button-background-color)",
    "--clay-dot-bg": "var(--clay-icon-button-background-color)",

    color: args?.tonal ? color.textAccent : color.text,
    svg: {
      color: args?.tonal ? color.iconAccent : color.icon,
    },

    "&:hover:not(:disabled)": {
      "--clay-icon-button-background-color": args?.tonal
        ? color.actionAccentTonalHover
        : color.actionAccentHover,
    },

    "&:active:not(:disabled)": {
      "--clay-icon-button-background-color": args?.tonal
        ? color.actionAccentTonalPressed
        : color.actionAccentPressed,
    },

    "&:disabled": {
      color: color.textDisabled,

      '&:not([data-imweb-loading="true"])': {
        "--clay-icon-button-background-color": args?.tonal
          ? color.actionAccentTonalDisabled
          : color.actionAccentDisabled,

        stroke: color.textDisabled,
      },

      '&[data-imweb-loading="true"]': {
        "--clay-icon-button-background-color": args?.tonal
          ? color.actionAccentTonalPressed
          : color.actionAccentPressed,

        color: args?.tonal ? color.textAccent : color.text,
        svg: {
          color: args?.tonal ? color.iconAccent : color.icon,
        },
      },
    },
  });

export const iconButtonCSS = <E extends ElementType>(props: IconButtonProps<E>) => {
  const { fullWidth, rounded, size = "medium" } = props;

  const variantStyle = match(props)
    .with({ variant: "primary" }, primary)
    .with({ variant: "secondary" }, secondary)
    .with({ variant: "critical" }, critical)
    .with({ variant: "accent" }, accent)
    .with({ variant: "outlined" }, outlined)
    .exhaustive();

  return css`
    display: inline-flex;
    justify-content: center;
    align-items: center;
    ${sizeStyles[size]};

    ${variantStyle};
    ${rounded && `border-radius: ${vars.rounded.full};`}

    width: ${fullWidth ? "100%" : "fit-content"};
    height: fit-content;

    transition-timing-function: ease-out;
    transition-duration: 100ms;
    transition-property: background-color, color;
  `;
};
